import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { breakpoints, Breakpoints, isWidthBelow } from '../../utils/ids-breakpoint-utils/ids-breakpoint-utils';
import { customElement, scss } from '../../core/ids-decorators';

import IdsBreakpointMixin from '../../mixins/ids-breakpoint-mixin/ids-breakpoint-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsFocusCaptureMixin from '../../mixins/ids-focus-capture-mixin/ids-focus-capture-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsPopupInteractionsMixin from '../../mixins/ids-popup-interactions-mixin/ids-popup-interactions-mixin';
import IdsXssMixin from '../../mixins/ids-xss-mixin/ids-xss-mixin';
import IdsElement from '../../core/ids-element';

import { stringToBool, isValidBooleanValue } from '../../utils/ids-string-utils/ids-string-utils';
import { waitForTransitionEnd, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { cssTransitionTimeout } from '../../utils/ids-timer-utils/ids-timer-utils';

import '../ids-popup/ids-popup';
import './ids-modal-button';
import IdsOverlay from './ids-overlay';
import type IdsModalButton from './ids-modal-button';

// Import Styles
import styles from './ids-modal.scss';
import type { IdsMessageSettings } from '../ids-message/ids-message';
import IdsButtonGroup from '../ids-button/ids-button-group';

type IdsModalFullsizeAttributeValue = null | 'null' | '' | 'none' | keyof Breakpoints | 'always';

type IdsModalButtonProps = {
  /* Set the id */
  id?: string;
  /* Set the button text */
  text: string;
  /* Set the button appearance */
  appearance?: string;
  /* Handle the click event */
  click: (e: Event, modal: IdsModal) => void;
  /* Set the button to the default button */
  isDefault?: boolean;
  /* Set the button to a cancel button */
  isCancel?: boolean;
};

export interface IdsModalSettings {
  /* Set the id */
  id?: string;
  /* Set the modal title */
  title: string;
  /* Set the modal contents */
  contents?: string | null | undefined;
  /* Set the modal button alignment [left, right] */
  buttonAlignment?: string;
  /* Set the href link */
  buttons?: IdsModalButtonProps[];
  /* Set the clickOutsideToClose setting */
  clickOutsideToClose?: boolean;
  /* Set the autoFocus setting */
  autoFocus?: boolean;
  /* Set the fullsize setting */
  fullsize?: IdsModalFullsizeAttributeValue;
  /* Set the scrollable setting */
  scrollable?: boolean;
  /* Set the showCloseButton setting */
  showCloseButton?: boolean;
}

const Base = IdsXssMixin(
  IdsBreakpointMixin(
    IdsFocusCaptureMixin(
      IdsKeyboardMixin(
        IdsPopupInteractionsMixin(
          IdsEventsMixin(
            IdsElement
          )
        )
      )
    )
  )
);

/**
 * IDS Modal Component
 * @type {IdsModal}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsFocusCaptureMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsPopupInteractionsMixin
 * @mixes IdsXssMixin
 * @part popup - the popup outer element
 * @part overlay - the inner overlay element
 */
@customElement('ids-modal')
@scss(styles)
export default class IdsModal extends Base {
  static zCount = 1020;

  shouldUpdate = false;

  onButtonClick?: (target: any) => void;

  ro?: ResizeObserver;

  /**
   * @property {Array<string>} Modal vetoable events
   */
  vetoableEventTypes: Array<string> = ['beforeshow', 'beforehide'];

  /**
   * @property {boolean} visible true if this Modal instance is visible.
   */
  #visible = false;

  globalKeydownListener = async (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        e.stopImmediatePropagation();
        if (this.nodeName === 'IDS-ERROR-PAGE') return;
        await this.hide();
        break;
      case 'Enter':
        if (!this.suppressEnterKey) {
          const path = e.composedPath();
          const inDataGrid = path.some(
            (el: EventTarget) => (el as HTMLElement)?.nodeName === 'IDS-DATA-GRID'
          );
          if (inDataGrid) break;

          // Check if a modal button currently has focus
          const focusedElement = e.target as HTMLElement;
          const isModalButtonFocused = focusedElement?.nodeName === 'IDS-MODAL-BUTTON' || focusedElement?.nodeName === 'IDS-BUTTON';

          if (isModalButtonFocused) {
            // If a button is focused, let it handle the Enter key normally (don't prevent default)
            break;
          }

          const defaultButton = this.querySelector('ids-modal-button[appearance="primary"]') as HTMLElement;
          if (defaultButton) {
            e.preventDefault();
            e.stopPropagation();
            defaultButton.click();
          }
        }
        break;
      default:
        break;
    }
  };

  constructor() {
    super();

    this.state ??= {};
    this.state.fullsize = '';
    this.state.overlay = null;
    this.state.scrollable = false;
    this.state.messageTitle = null;
  }

  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.CLICK_OUTSIDE_TO_CLOSE,
      attributes.AUTO_FOCUS,
      attributes.HEIGHT,
      attributes.FULLSIZE,
      attributes.MESSAGE_TITLE,
      attributes.OVERFLOW,
      attributes.SCROLLABLE,
      attributes.SHOW_CLOSE_BUTTON,
      attributes.SUPPRESS_ENTER_KEY,
      attributes.VISIBLE,
      attributes.WIDTH
    ];
  }

  connectedCallback(): void {
    super.connectedCallback?.();

    if (this.popup) {
      this.popup.setAttribute(attributes.TYPE, 'modal');
      this.popup.setAttribute(attributes.ANIMATED, 'false');
      this.popup.setAttribute(attributes.ANIMATION_STYLE, 'none');
      this.popup.onOutsideClick = () => {};
    }

    // Update Outer Modal Parts
    this.#refreshOverlay();
    this.#refreshOverflow();

    this.attachEventHandlers();
    this.shouldUpdate = true;
    this.setResize();
    this.#setFullsizeDefault();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.#setFocusIfVisible();

    this.setAttribute(htmlAttributes.ROLE, 'dialog');
    this.setAttribute(htmlAttributes.ARIA_LABEL, this.ariaLabelContent);
    this.toggleAttribute(htmlAttributes.ARIA_LABEL, !!this.getAttribute(htmlAttributes.ARIA_LABEL));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#clearBreakpointResponse();
    this.ro?.disconnect();
    this.ro = undefined;
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    const extraClass = this.name !== 'ids-modal' ? this.name : '';
    const extraContentClass = extraClass ? ` ${extraClass}-content` : '';
    const extraHeaderClass = extraClass ? ` ${extraClass}-header` : '';
    const extraFooterClass = extraClass ? ` ${extraClass}-footer` : '';
    const footerHidden = this.buttons.length ? '' : ' hidden';

    return `<ids-popup part="modal"
      class="ids-modal"
      type="modal"
      position-style="viewport">
      <div class="ids-modal-container" slot="content">
        <div class="ids-modal-header${extraHeaderClass}">
          <slot name="title">
            <ids-text id="title-internal" type="h2" font-size="24">${this.messageTitle ?? ''}</ids-text>
          </slot>
        </div>
        <div class="ids-modal-content${extraContentClass}">
          <slot></slot>
        </div>
        <div class="ids-modal-footer${extraFooterClass}" ${footerHidden}>
          <slot name="buttons"></slot>
        </div>
      </div>
    </ids-popup>`;
  }

  private setResize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.ro?.disconnect();

    if (!this.ro) {
      this.ro = new ResizeObserver(() => {
        this.setScrollable();
        this.#repositionPopups();
      });
    }

    this.ro.observe(this);
  }

  private setScrollable(): void {
    if (!this.scrollable) return;
    const el = this.modalContentEl;
    if (!el) return;
    el.classList.toggle('has-scrollbar', el.scrollHeight > el.clientHeight);
  }

  /**
   * Set whether to focus the first input element instead of the close button
   * @param {boolean} val true to focus first input (default false)
   */
  set autoFocus(val: boolean) {
    this.toggleAttribute(attributes.AUTO_FOCUS, stringToBool(val));
  }

  /**
   * Get whether to focus the first input element instead of the close button
   * @returns {boolean} true if first input should be focused
   */
  get autoFocus(): boolean {
    return this.hasAttribute(attributes.AUTO_FOCUS);
  }

  /**
   * Whether or not to focus the target element for modal. Default is true.
   * @param {boolean | string | null} val true if focus target should be set
   */
  set focusTarget(val: boolean | string | null) {
    this.setAttribute(attributes.FOCUS_TARGET, String(val));
  }

  /**
   * Get whether to focus the target element for modal
   * @returns {boolean} true if focus target should be set (default true)
   */
  get focusTarget(): boolean {
    const focusTarget = this.getAttribute(attributes.FOCUS_TARGET);
    if (focusTarget) return stringToBool(focusTarget);

    return true;
  }

  /**
   * Get the content for ARIA labels
   * @returns {string} concatenating the status and title together
   */
  get ariaLabelContent(): string {
    let ariaLabel = this.messageTitle;
    if (!ariaLabel) ariaLabel = this.querySelector('ids-toolbar [type="title"]')?.textContent || '';
    return ariaLabel?.replaceAll('\n', '')?.trim();
  }

  /**
   * Get the currently slotted buttons
   * @returns {NodeListOf<IdsModalButton>} the modal buttons
   */
  get buttons(): NodeListOf<IdsModalButton> {
    return this.querySelectorAll<IdsModalButton>('[slot="buttons"] > ids-button-group-section > ids-modal-button');
  }

  /**
   * Get the current button group
   * @returns {IdsButtonGroup | null} the button group
   */
  get buttonSet(): IdsButtonGroup | null {
    return this.querySelector<IdsButtonGroup>('ids-button-group') ?? null;
  }

  /**
   * Get the slotted title element or the default title slot
   * @returns {Element | undefined} the title element
   */
  get titleSlot(): Element | undefined {
    return this.querySelector('[slot="title"]') ?? this.shadowRoot?.querySelector('#title-internal') ?? undefined;
  }

  /**
   * Get the Modal's content wrapper element
   * @returns {HTMLElement | null} the content wrapper element
   */
  get modalContentEl(): HTMLElement | null {
    return this.container?.querySelector('.ids-modal-content') || null;
  }

  /**
   * Get the breakpoint at which the Modal will change from normal mode to fullsize mode
   * @returns {IdsModalFullsizeAttributeValue} the fullsize breakpoint
   */
  get fullsize(): IdsModalFullsizeAttributeValue {
    return this.state.fullsize;
  }

  /**
   * @param {IdsModalFullsizeAttributeValue} val the breakpoint at which
   * the Modal will change from normal mode to fullsize mode
   */
  set fullsize(val: IdsModalFullsizeAttributeValue) {
    const current = this.fullsize;
    const makeFullsize = (doFullsize: boolean) => {
      if (!this.popup) return;

      this.popup.classList[doFullsize ? 'add' : 'remove'](attributes.FULLSIZE);
      this.popup.setAttribute(attributes.WIDTH, doFullsize ? '100%' : '');
      this.popup.setAttribute(attributes.HEIGHT, doFullsize ? '100%' : '');
      if (this.popup?.place) this.popup.place();
      if (this.popup?.open) {
        this.setScrollable();
        this.popup?.correct3dMatrix();
      }
    };

    const safeVal = `${val}`;
    if (current !== val && this.popup) {
      switch (val) {
        case 'always':
          this.#clearBreakpointResponse();
          this.state.fullsize = 'always';
          this.popup?.classList.add(`can-fullsize`);
          makeFullsize(true);
          break;
        case null:
        case 'null':
        case '':
          this.state.fullsize = '';
          this.#clearBreakpointResponse();
          this.removeAttribute(attributes.FULLSIZE);
          this.popup?.classList.remove('can-fullsize');
          makeFullsize(false);
          break;
        default:
          if (Object.keys(breakpoints).includes(safeVal)) {
            this.state.fullsize = safeVal;
            this.setAttribute(attributes.FULLSIZE, safeVal);
            this.popup?.classList.add(`can-fullsize`);
            this.respondDown = safeVal;
            this.onBreakpointDownResponse = (detectedBreakpoint: keyof Breakpoints, matches: boolean) => {
              makeFullsize(matches);
            };
          }
          this.respondToCurrentBreakpoint();
          break;
      }
    }
  }

  /**
   * Sets whether the close button should be shown
   * @param {boolean} val true to show the close button, false to hide it
   */
  set showCloseButton(val: boolean) {
    const show = stringToBool(val);

    this.toggleAttribute(attributes.SHOW_CLOSE_BUTTON, show);
    if (show) {
      this.#attachCloseButton();
    } else {
      this.#removeCloseButton();
    }
  }

  /**
   * Get whether the close button is shown
   * @returns {boolean} true if close button is shown
   */
  get showCloseButton(): boolean {
    return this.hasAttribute(attributes.SHOW_CLOSE_BUTTON);
  }

  /**
   * Set whether to suppress the Enter key from dismissing the modal
   * @param {boolean} val true to suppress Enter key behavior
   */
  set suppressEnterKey(val: boolean) {
    if (!isValidBooleanValue(val)) return;
    const suppress = stringToBool(val);
    this.toggleAttribute(attributes.SUPPRESS_ENTER_KEY, suppress);
  }

  /**
   * Get whether the Enter key is suppressed from dismissing the modal
   * @returns {boolean} true if Enter key is suppressed
   */
  get suppressEnterKey(): boolean {
    return this.hasAttribute(attributes.SUPPRESS_ENTER_KEY);
  }

  /**
   * Get the close button element
   * @returns {HTMLElement | null} the close button
   */
  get closeButton(): HTMLElement | null {
    return this.container?.querySelector<HTMLElement>('.modal-control-close') ?? null;
  }

  /**
   * Removes established callbacks for responding to breakpoints, if set
   */
  #clearBreakpointResponse(): void {
    if (this.respondDown) {
      this.respondDown = null;
    }
    if (this.onBreakpointDownResponse) {
      this.onBreakpointDownResponse = undefined;
    }
  }

  /**
   * Runs on connectedCallback or any refresh to adjust the `fullsize` attribute, if set
   */
  #setFullsizeDefault(): void {
    // Default all Modals to `sm` fullsize
    if (this.hasAttribute(attributes.FULLSIZE)) {
      this.fullsize = this.getAttribute(attributes.FULLSIZE);
    } else {
      this.fullsize = 'sm';
    }
  }

  /**
   * Get the current overlay element
   * @returns {IdsOverlay} either an external overlay element, or none
   */
  get overlay(): IdsOverlay {
    return this.state.overlay || this.shadowRoot?.querySelector<IdsOverlay>('ids-overlay');
  }

  /**
   * Sets the overlay element for the modal
   * @param {IdsOverlay} val an overlay element
   */
  set overlay(val: IdsOverlay) {
    this.state.overlay = val instanceof HTMLElement ? val : null;
    this.#refreshOverlay();
  }

  /**
   * Get the content of the message's title
   * @returns {string} the message title
   */
  get messageTitle(): string {
    const titleSlot = this.titleSlot;
    let titleText = titleSlot?.textContent || '';
    titleText = titleText.replaceAll('\n', '')?.trim();
    return titleText || this.state.messageTitle;
  }

  /**
   * Sets the content of the message's title
   * @param {string} val the new content to be used as the message's title
   */
  set messageTitle(val: string) {
    let messageVal = this.xssSanitize(val);
    const currentVal = this.state.messageTitle;
    if (currentVal === messageVal) return;

    if (typeof messageVal === 'string' && messageVal.length) {
      messageVal = messageVal?.replaceAll('\n', '')?.trim();
      this.state.messageTitle = messageVal;
      this.setAttribute(attributes.MESSAGE_TITLE, messageVal);
      this.setAttribute(htmlAttributes.ARIA_LABEL, messageVal);
    } else {
      this.state.messageTitle = '';
      this.removeAttribute(attributes.MESSAGE_TITLE);
      this.removeAttribute(htmlAttributes.ARIA_LABEL);
    }

    if (this.titleSlot) this.titleSlot.innerHTML = this.state.messageTitle;
  }

  /**
   * Sets modal scrollable setting
   * @param {boolean | string} val scrollable flag
   */
  set scrollable(val: boolean | string) {
    const isScrollable = stringToBool(val);
    this.toggleAttribute(attributes.SCROLLABLE, isScrollable);
    this.container?.classList.toggle(attributes.SCROLLABLE, isScrollable);
    this.state.scrollable = isScrollable;
  }

  /**
   * Get the modal scrollable setting
   * @returns {boolean | string} true if scrollable
   */
  get scrollable(): boolean | string {
    return this.state.scrollable;
  }

  /**
   * Renders or Removes a correct `aria-label` attribute on the Modal about its contents.
   * @returns {void}
   */
  refreshAriaLabel(): void {
    const title = this.ariaLabelContent;
    if (title) {
      this.setAttribute(htmlAttributes.ARIA_LABEL, title);
      return;
    }
    this.removeAttribute(htmlAttributes.ARIA_LABEL);
  }

  /**
   * Refreshes the state of the Modal footer, hiding/showing it
   * @returns {void}
   */
  #refreshModalFooter() {
    if (!this.container) return;
    const footerEl = this.container.querySelector('.ids-modal-footer');

    if (this.buttons.length) {
      footerEl?.removeAttribute(attributes.HIDDEN);
    } else {
      footerEl?.setAttribute(attributes.HIDDEN, '');
    }
  }

  /**
   * Get whether the Modal is visible
   * @returns {boolean | string} true if visible
   */
  get visible(): boolean | string {
    return this.#visible;
  }

  /**
   * Sets whether the Modal is visible
   * @param {boolean | string} val true if the Modal is visible
   */
  set visible(val: boolean | string) {
    const trueVal = stringToBool(val);
    if (this.#visible !== trueVal) {
      this.#visible = trueVal;
      this.toggleAttribute(attributes.VISIBLE, trueVal);
      this.#refreshVisibility(trueVal);
    }
  }

  /**
   * Creates and inserts a button group template for modal buttons
   * @param {IdsModalButtonProps[] | NodeListOf<IdsModalButton>} buttons - Button configuration objects or existing
   *   modal button elements
   * @param {string} [buttonAlignment] - Button alignment ('left' for row direction, otherwise row-reverse)
   * @private
   */
  #createButtonGroupTemplate(buttons: IdsModalButtonProps[] | NodeListOf<IdsModalButton>, buttonAlignment?: string) {
    const direction = buttonAlignment === 'left' ? 'row' : 'row-reverse';

    const buttonGroup = document.createElement('ids-button-group');
    buttonGroup.setAttribute(htmlAttributes.SLOT, 'buttons');
    buttonGroup.setAttribute(attributes.DIRECTION, direction);

    const buttonGroupSection = document.createElement('ids-button-group-section');
    buttonGroupSection.setAttribute('gap', '8');

    // Service
    if (Array.isArray(buttons)) {
      buttons.sort((a, b) => (a.isDefault ? 1 : 0) - (b.isDefault ? 1 : 0));

      buttons.forEach((button, i) => {
        const btnId = button.id || `btn-${i + 1}`;
        const modalButton = document.createElement('ids-modal-button');
        modalButton.id = btnId;
        modalButton.setAttribute(attributes.APPEARANCE, button.appearance || (button.isDefault ? 'primary' : 'secondary'));
        if (button.isCancel) modalButton.setAttribute(attributes.CANCEL, '');
        modalButton.innerHTML = `<span>${button.text}</span>`;
        buttonGroupSection.appendChild(modalButton);
      });
    } else {
      // Regular
      for (const btn of buttons) {
        btn.removeAttribute(htmlAttributes.SLOT);
        buttonGroupSection.appendChild(btn);
      }
    }

    buttonGroup.appendChild(buttonGroupSection);
    this.appendChild(buttonGroup);

    if (Array.isArray(buttons)) {
      buttons.forEach((button, index) => {
        const btnId = button.id || `btn-${index + 1}`;
        document.querySelector(`#${btnId}`)?.addEventListener('click', (e) => button.click(e, this));
      });
    }
  }

  isServiceMessage = false;

  /**
   * Shows the modal with possibity to veto the promise
   * @param {IdsModalSettings} [settings] optional settings for the modal (used when a service)
   * @returns {Promise<void>}
   */
  async show(settings?: IdsModalSettings | IdsMessageSettings): Promise<void> {
    // Trigger a veto-able `beforeshow` event.
    if (!this.triggerVetoableEvent('beforeshow')) {
      return;
    }

    if (settings) {
      this.isServiceMessage = true;

      const {
        id,
        contents,
        title,
        buttons,
        clickOutsideToClose,
        autoFocus: autoFocusVal,
        fullsize,
        scrollable,
        showCloseButton,
        buttonAlignment,
      }: any = settings || {};

      if (id) this.setAttribute('id', id);
      if (title) this.messageTitle = title;
      if (contents) this.insertAdjacentHTML('beforeend', `${contents}`);
      if (clickOutsideToClose) this.clickOutsideToClose = clickOutsideToClose;
      if (autoFocusVal !== undefined) this.autoFocus = autoFocusVal;
      if (fullsize) this.fullsize = fullsize;
      if (scrollable) this.scrollable = scrollable;
      if (showCloseButton) this.showCloseButton = showCloseButton;

      if (buttons) this.#createButtonGroupTemplate(buttons, buttonAlignment);
    }

    if (!this.buttonSet) {
      const modalBtns = this.querySelectorAll<IdsModalButton>('ids-modal-button[slot="buttons"]');
      if (modalBtns) this.#createButtonGroupTemplate(modalBtns);
    }

    // Setting the value reruns this method, so exit afterward
    if (!this.visible) {
      this.visible = true;
      return;
    }

    // Check if buttons have been added/removed, and adjust the footer visibility (if applicable)
    if (this.container) {
      const footer = this.container.querySelector('.ids-modal-footer');
      footer?.toggleAttribute(attributes.HIDDEN, !this.buttons.length);
    }

    // Animation-in needs the Modal to appear in front (z-index), so this occurs on the next tick
    this.style.setProperty('z-index', String(++IdsModal.zCount));
    if (this.overlay) this.overlay.visible = true;
    if (this.popup) {
      this.popup.visible = true;
      if (this.popup.animated && this.popup.container) {
        await waitForTransitionEnd(this.popup.container, 'opacity');
      }
      this.setScrollable();
      this.#refreshOverflow();
      this.#adjustDropdownWidths();
      requestAnimationFrame(() => this.popup?.correct3dMatrix());
    }
    if (this.overlay) this.overlay.visible = true;

    // Focus the correct element
    this.capturesFocus = true;
    if (this.focusTarget) {
      this.setFocus(this.#getFocusableElementIndex());
    }

    this.addOpenEvents();
    this.triggerEvent('show', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });

    if (this.popup) {
      this.popup.removeAttribute('animated');
    }

    this.respondToCurrentBreakpoint();
    this.triggerEvent('aftershow', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });
  }

  /**
   * Hides the modal with possibity to veto the promise
   * @returns {Promise<void>}
   */
  async hide(): Promise<void> {
    // Trigger a veto-able `beforehide` event.
    if (!this.triggerVetoableEvent('beforehide')) return;

    // Setting the value reruns this method, so exit afterward
    if (this.visible) {
      this.visible = false;
      return;
    }

    const popupElem = this.popup;

    if (popupElem && popupElem.getAttribute(attributes.ANIMATED) === 'true') popupElem.animated = true;

    this.removeOpenEvents();
    if (this.overlay) this.overlay.visible = false;
    if (popupElem) popupElem.visible = false;

    // Animation-out can wait for the opacity transition to end before changing z-index.
    if (popupElem && popupElem.container && popupElem.animated) {
      await waitForTransitionEnd(popupElem.container, 'opacity');
    }
    this.style.zIndex = '';
    --IdsModal.zCount;

    // Disable focus capture
    this.capturesFocus = false;

    this.triggerEvent('hide', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });

    this.#setTargetFocus();

    this.triggerEvent('afterhide', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });

    if (this.isServiceMessage) {
      this.remove();
    }
  }

  /**
   * Overrides `addOpenEvents` from the OpenEvents mixin to add additional "Escape" key handling
   * @private
   */
  addOpenEvents(): void {
    this.removeOpenEvents();

    // Adds a global event listener for the Keydown event on the body to capture close via Escape
    // (NOTE cannot use IdsEventsMixin here due to scoping)
    document.addEventListener('keydown', this.globalKeydownListener);

    // If a Modal Button is clicked, fire an optional callback
    const buttonSlot = this.container?.querySelector('slot[name="buttons"]');
    this.offEvent('click.buttons');
    this.onEvent('click.buttons', buttonSlot, async (e: MouseEvent) => {
      await this.handleButtonClick(e);
    });
  }

  /**
   * Overrides `removeOpenEvents` from the OpenEvents mixin to remove "Escape" key handling
   * @private
   */
  removeOpenEvents(): void {
    document.removeEventListener('keydown', this.globalKeydownListener);
    this.unlisten('Escape');
    this.offEvent('click.buttons');
  }

  /**
   * Refreshes the state of the overlay used behind the modal.  If a shared overlay isn't applied,
   * an internal one is generated and applied to the ShadowRoot.
   * @returns {void}
   */
  #refreshOverlay(): void {
    const externalOverlay = this.state.overlay;
    this.shadowRoot?.querySelector('ids-overlay')?.remove();
    this.shadowRoot?.prepend(externalOverlay || document.createElement('ids-overlay'));
  }

  /**
   * @param {boolean} val if true, makes the Modal visible to the user
   * @returns {void}
   */
  #refreshVisibility(val: boolean): void {
    if (val) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.show();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hide();
    }
  }

  /**
   * Focuses the defined target element, if applicable
   * @returns {void}
   */
  #setTargetFocus(): void {
    if (this.target && this.focusTarget) {
      this.target.focus();
    }
  }

  /**
   * Runs calculation-sensitive routines when the entire DOM has loaded
   */
  async #setFocusIfVisible() {
    this.visible = stringToBool(this.getAttribute('visible'));
    if (this.visible && this.focusTarget) {
      this.setFocus(this.#getFocusableElementIndex());
    }
  }

  /**
   * Sets up overall events
   * @private
   */
  attachEventHandlers(): void {
    const titleSlot = this.container?.querySelector<HTMLSlotElement>('slot[name="title"]');
    const buttonSlot = this.container?.querySelector<HTMLSlotElement>('slot[name="buttons"]');

    // Stagger these one frame to prevent them from occuring
    // immediately when the component invokes
    window.requestAnimationFrame(() => {
      this.offEvent('slotchange.title', titleSlot);
      this.onEvent('slotchange.title', titleSlot, () => {
        const titleNodes = titleSlot?.assignedElements();

        if (titleNodes?.length) {
          this.messageTitle = titleNodes[0].textContent ?? '';
          titleNodes.slice(1).forEach((elem) => elem.remove());
        }
      });

      this.offEvent('slotchange.buttonset', buttonSlot);
      this.onEvent('slotchange.buttonset', buttonSlot, () => {
        this.#refreshModalFooter();
      });

      if (this.visible) {
        this.addOpenEvents();
      }
    });

    // Set up all the events specifically-related to the "trigger" type
    this.refreshTriggerEvents();
  }

  /**
   * Handles when Modal Button is clicked.
   * @param {any} e the original event object
   */
  async handleButtonClick(e: Event): Promise<void> {
    await cssTransitionTimeout(200);

    if (!(e.target instanceof HTMLElement)) return;

    // Find the actual button element (could be nested inside a span or other wrapper)
    const clickedButton = e.target.closest('ids-button, ids-modal-button');

    if (typeof this.onButtonClick === 'function' && clickedButton && (clickedButton.nodeName === 'IDS-BUTTON' || clickedButton.nodeName === 'IDS-MODAL-BUTTON')) {
      this.onButtonClick(clickedButton as IdsModalButton);
    }

    // If this IdsModalButton has a `cancel` prop, treat
    // it as a `cancel` button and hide.
    const modalBtn = e.target.closest('ids-modal-button') as IdsModalButton;
    if (modalBtn?.cancel) {
      await this.hide();
    }
  }

  /**
   * Handle `onTriggerClick` from IdsPopupInteractionsMixin
   * @returns {void}
   */
  onTriggerClick(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.show();
  }

  /**
   * Handle `onOutsideClick` from IdsPopupOpenEventsMixin
   * @param {MouseEvent} e the original click event
   * @returns {void}
   */
  onOutsideClick(e: MouseEvent): void {
    if (!e?.target || e.composedPath()?.includes(this.popup as HTMLElement)) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.hide();
  }

  /**
   * Get the click-outside-to-close attribute
   * @returns {boolean | string} true if click outside to close is enabled
   */
  get clickOutsideToClose(): boolean | string {
    return stringToBool(this.getAttribute(attributes.CLICK_OUTSIDE_TO_CLOSE));
  }

  /**
   * Set whether or not to allow the modal to close by clicking outside
   * @param {boolean | string} val click-outside-to-close attribute value
   */
  set clickOutsideToClose(val: boolean | string) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.popup!.onOutsideClick = this.onOutsideClick.bind(this);
    } else {
      this.popup!.onOutsideClick = () => {};
    }
    this.toggleAttribute(attributes.CLICK_OUTSIDE_TO_CLOSE, boolVal);
  }

  /**
   * Set the modal height to a fixed or fluid value. If using do not use the fullsize or respond down settings.
   * @param {string | null} val height to use as css property
   */
  set height(val: string | null | undefined) {
    if (val) {
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.height = val;
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.minHeight = val;
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.maxHeight = `calc(${val} - 110px)`;
      this.setAttribute(attributes.HEIGHT, val);
    } else {
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.removeProperty('height');
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.removeProperty('maxHeight');
      this.removeAttribute(attributes.HEIGHT);
    }
  }

  /**
   * Get the modal height
   * @returns {string | null | undefined} the height value
   */
  get height(): string | null | undefined {
    return this.getAttribute(attributes.HEIGHT)?.toString();
  }

  /**
   * Set the modal width to a fixed or fluid value. If using do not use the fullsize or respond down settings.
   * @param {string | null} val width to use as css property
   */
  set width(val: string | null | undefined) {
    if (val) {
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.width = val;
      this.setAttribute(attributes.WIDTH, val);
    } else {
      this.popup!.querySelector<HTMLDivElement>('.ids-modal-container')!.style.removeProperty('width');
      this.removeAttribute(attributes.WIDTH);
    }
  }

  /**
   * Get the modal width
   * @returns {string | null | undefined} the width value
   */
  get width(): string | null | undefined {
    return this.getAttribute(attributes.WIDTH)?.toString();
  }

  /**
   * Add button with icon to the modal
   * Reusing ids-modal-button component with cancel attribute and extra css class to change appearance
   */
  #attachCloseButton() {
    this.#removeCloseButton();

    const element = `<ids-button icon="close" class="modal-control-close" cancel></ids-button>`;
    this.container?.querySelector('.ids-modal-container')?.insertAdjacentHTML('afterbegin', element);

    // attach close button handler
    this.onEvent('click.modal-close', this.closeButton, () => this.hide());
  }

  #removeCloseButton() {
    const closeButton = this.closeButton;
    this.offEvent('click.modal-close', closeButton);
    closeButton?.remove();
  }

  /**
   * Sets the overflow behavior for the modal content
   * @param {string | null} val overflow value ('visible', 'hidden', 'auto', 'scroll')
   */
  set overflow(val: string | null | undefined) {
    if (val) {
      this.setAttribute(attributes.OVERFLOW, val);
    } else {
      this.removeAttribute(attributes.OVERFLOW);
    }
    this.#refreshOverflow();
  }

  /**
   * Get the overflow behavior for the modal content
   * @returns {string | null | undefined} the overflow value
   */
  get overflow(): string | null | undefined {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Refreshes the overflow setting on the popup content wrapper
   * @private
   */
  #refreshOverflow(): void {
    if (!this.popup) return;

    const contentWrapper = this.popup.shadowRoot?.querySelector('.content-wrapper');
    if (!contentWrapper) return;

    const overflowValue = this.overflow;
    if (overflowValue === 'visible') {
      (contentWrapper as HTMLElement).style.overflow = 'visible';
      this.popup.container?.classList.add('overflow-visible');
      this.#adjustDropdownWidths();
    } else if (overflowValue) {
      (contentWrapper as HTMLElement).style.overflow = overflowValue;
      this.popup.container?.classList.remove('overflow-visible');
    } else {
      (contentWrapper as HTMLElement).style.removeProperty('overflow');
      this.popup.container?.classList.remove('overflow-visible');
    }
  }

  /**
   * Adjusts dropdown widths to match their trigger elements
   * @private
   */
  #adjustDropdownWidths(): void {
    const searchFields = this.querySelectorAll('ids-search-field');
    const isDesktop = !isWidthBelow('md').matches;

    searchFields.forEach((field: any) => {
      if (field.popup) {
        if (isDesktop) {
          const fieldWidth = field.getBoundingClientRect().width;
          field.popup.style.minWidth = `${fieldWidth}px`;
          field.popup.style.removeProperty('width');
        } else {
          field.popup.style.removeProperty('min-width');
          field.popup.style.removeProperty('width');
        }
      }
    });
  }

  /**
   * Repositions any open popups within the modal when it's resized
   * @private
   */
  #repositionPopups(): void {
    const searchFields = this.querySelectorAll('ids-search-field');

    searchFields.forEach((field: any) => {
      if (field.popup && field.popup.visible) {
        field.popup.place();
      }
    });
  }

  /**
   * @returns {number} the index of the first focusable element in the modal content skipping the toolbar
   */
  #getFocusableElementIndex(): number {
    if (this.autoFocus) {
      // Find first input element, skipping close button and toolbar elements
      const inputSelectors = ['input', 'ids-input', 'textarea', 'ids-textarea', 'select', 'ids-dropdown'];
      const firstInputIndex = this.focusableElements?.findIndex((item: HTMLElement) => {
        const isInput = inputSelectors.some((selector) => item.matches(selector));
        const isCloseButton = item.classList.contains('modal-control-close');
        const isInToolbar = getClosest(item, 'ids-toolbar');
        return isInput && !isCloseButton && !isInToolbar;
      });

      if (firstInputIndex !== undefined && firstInputIndex >= 0) {
        return firstInputIndex;
      }
    }

    return this.focusableElements?.findIndex((item: HTMLElement) => !getClosest(item, 'ids-toolbar')) || 0;
  }
}
